// src/utils/unitActivity.js
// "Studied" vs "skipped" for one unit (a top-level item in a lesson: a
// package or a standalone unit — what the learner sees as one step).
//
// Definition (measurement framework, decided 2026-10-09):
//   STUDIED  = went past the first page, OR interacted (answered, flipped,
//              recorded …), OR spent at least STUDY_SECONDS on it in total.
//   SKIPPED  = opened, but none of the above.
//
// Only the raw signals are saved (learnerProgress.unitActivity.<unitId>), and
// "studied" is worked out when reading. So the threshold can be tuned later
// and old data is simply re-classified — nothing needs migrating.

export const STUDY_SECONDS = 60;

// Longest stretch counted in one go — a unit left open on a forgotten tab
// shouldn't add hours.
export const MAX_SECONDS_PER_STRETCH = 15 * 60;

// activity: { seconds, pagesBeyondFirst, interacted, opens, … } or undefined
// → 'studied' | 'skipped' | 'not_opened'
export function classifyUnit(activity) {
  if (!activity) return 'not_opened';
  if (activity.pagesBeyondFirst || activity.interacted) return 'studied';
  if ((activity.seconds || 0) >= STUDY_SECONDS) return 'studied';
  return 'skipped';
}

// Counts for one lesson.
//   unitActivity — learnerProgress.unitActivity (map unitId → activity)
//   lessonId     — the lesson to count
//   totalUnits   — number of units in the lesson (learnerProgress.lessonTotals[lessonId])
//   openedBefore — ids of units opened before tracking started (optional);
//                  they count as "opened earlier", never guessed as studied/skipped
export function countLesson(unitActivity = {}, lessonId, totalUnits = 0, openedBefore = []) {
  let studied = 0;
  let skipped = 0;
  const tracked = new Set();
  Object.entries(unitActivity).forEach(([unitId, a]) => {
    if (!a || a.lessonId !== lessonId) return;
    tracked.add(unitId);
    if (classifyUnit(a) === 'studied') studied++;
    else skipped++;
  });
  const openedEarlier = openedBefore.filter(id => !tracked.has(id)).length;
  const opened = studied + skipped + openedEarlier;
  const notOpened = Math.max(0, totalUnits - opened);
  return { studied, skipped, openedEarlier, notOpened, total: Math.max(totalUnits, opened) };
}